// The building · floor · apartment navigator (U3, docs/product-ux 07): its address, floor grouping,
// filters, search and keyboard moves. Pure functions over the server's answer: no status is
// computed here (the server sends stage, defects and exceptions).
import type {
  NavigatorApartment,
  NavigatorBuilding,
  NavigatorLevel,
  NavigatorResponse,
} from '@sivan/shared';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** #structure[/<project>[/<building>[/<apartment>]]]: ids only; the apartment last opened. */
export interface NavigatorLocation {
  readonly projectId: string | null;
  readonly buildingId: string | null;
  readonly apartmentId: string | null;
}

export function parseNavigatorLocation(hash: string): NavigatorLocation {
  const [section, projectId, buildingId, apartmentId] = hash.replace(/^#/, '').split('/');
  const id = (value: string | undefined) =>
    value !== undefined && UUID.test(value) ? value : null;
  if (section !== 'structure' || id(projectId) === null)
    return { projectId: null, buildingId: null, apartmentId: null };
  const building = id(buildingId);
  return {
    projectId: id(projectId),
    buildingId: building,
    apartmentId: building === null ? null : id(apartmentId),
  };
}

export function formatNavigatorLocation(location: NavigatorLocation): string {
  const parts = ['#structure'];
  if (location.projectId !== null) {
    parts.push(location.projectId);
    if (location.buildingId !== null) {
      parts.push(location.buildingId);
      if (location.apartmentId !== null) parts.push(location.apartmentId);
    }
  }
  return parts.join('/');
}

/** A floor of a building with its apartments; `level` null: apartments without a floor. */
export interface FloorGroup {
  readonly key: string;
  readonly level: NavigatorLevel | null;
  readonly label: string;
  readonly apartments: readonly NavigatorApartment[];
}

/** The floors, top first (the stored order), empty floors included; apartments without a floor last. */
export function floorsOf(building: NavigatorBuilding): FloorGroup[] {
  const floors: FloorGroup[] = building.levels.map((level) => ({
    key: level.id,
    level,
    label: level.label,
    apartments: building.apartments.filter((a) => a.levelId === level.id),
  }));
  const loose = building.apartments.filter(
    (a) => a.levelId === null || !building.levels.some((l) => l.id === a.levelId),
  );
  if (loose.length > 0)
    floors.push({ key: 'no-floor', level: null, label: 'ללא קומה', apartments: loose });
  return floors;
}

export const FILTERS = [
  { id: 'all', label: 'הכל', test: () => true },
  {
    id: 'not_started',
    label: 'טרם החלה',
    test: (a: NavigatorApartment) => a.stage === 'not_started' || a.stage === 'scheduled',
  },
  {
    id: 'initial',
    label: 'ראשונית',
    test: (a: NavigatorApartment) =>
      a.stage === 'initial_in_progress' || a.stage === 'initial_summary',
  },
  {
    id: 'final',
    label: 'סופית',
    test: (a: NavigatorApartment) => a.stage === 'final_in_progress' || a.stage === 'final_summary',
  },
  {
    id: 'defects',
    label: 'ליקויים פתוחים',
    test: (a: NavigatorApartment) =>
      a.defects !== null && (a.defects.open > 0 || a.defects.awaiting > 0),
  },
  {
    id: 'exceptions',
    label: 'חריגים',
    test: (a: NavigatorApartment) => a.exceptions.length > 0,
  },
] as const;
export type FilterId = (typeof FILTERS)[number]['id'];

export const filterById = (id: FilterId) => FILTERS.find((f) => f.id === id) ?? FILTERS[0];

/** How many apartments of `apartments` each filter matches. */
export function filterCounts(
  apartments: readonly NavigatorApartment[],
): Readonly<Record<FilterId, number>> {
  return Object.fromEntries(
    FILTERS.map((f) => [f.id, apartments.filter((a) => f.test(a)).length]),
  ) as Record<FilterId, number>;
}

/** The search text, normalized: digits and letters only matter; case and spaces do not. */
const normalize = (text: string) => text.trim().replace(/\s+/g, '').toLowerCase();

/** True when `query` is the apartment's current number or its original (source) number. */
export function matchesSearch(apartment: NavigatorApartment, query: string): boolean {
  const q = normalize(query);
  if (q === '') return false;
  return (
    normalize(apartment.number) === q ||
    (apartment.sourceNumber !== null && normalize(apartment.sourceNumber) === q)
  );
}

/** Every apartment of the project matching the search, building by building. */
export function searchProject(
  data: NavigatorResponse,
  query: string,
): { readonly building: NavigatorBuilding; readonly apartment: NavigatorApartment }[] {
  return data.buildings.flatMap((building) =>
    building.apartments
      .filter((apartment) => matchesSearch(apartment, query))
      .map((apartment) => ({ building, apartment })),
  );
}

export type GridKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End';

/**
 * The apartment a key moves to in the floor grid (right to left): left is the next apartment of
 * the floor, right the previous one; up and down the nearest apartment on the floor above or below
 * (skipping floors without apartments); Home and End the first and last of the floor.
 */
export function moveInGrid(
  floors: readonly (readonly string[])[],
  current: string,
  key: GridKey,
): string {
  const row = floors.findIndex((ids) => ids.includes(current));
  if (row < 0) return current;
  const ids = floors[row] ?? [];
  const column = ids.indexOf(current);
  switch (key) {
    case 'ArrowLeft':
      return ids[Math.min(column + 1, ids.length - 1)] ?? current;
    case 'ArrowRight':
      return ids[Math.max(column - 1, 0)] ?? current;
    case 'Home':
      return ids[0] ?? current;
    case 'End':
      return ids[ids.length - 1] ?? current;
    case 'ArrowUp':
    case 'ArrowDown': {
      const step = key === 'ArrowUp' ? -1 : 1;
      for (let r = row + step; r >= 0 && r < floors.length; r += step) {
        const next = floors[r] ?? [];
        if (next.length > 0) return next[Math.min(column, next.length - 1)] ?? current;
      }
      return current;
    }
  }
}

/** The accessible name of an apartment: number, stage, defects and exceptions, in words. */
export function describeApartment(
  apartment: NavigatorApartment,
  labels: {
    readonly stage: string;
    readonly defects: string | null;
    readonly exceptions: readonly string[];
  },
): string {
  return [
    `דירה ${apartment.number}`,
    labels.stage,
    ...(labels.defects === null ? [] : [labels.defects]),
    ...labels.exceptions,
  ].join(', ');
}
